/**
 * Helpers para construir URLs de edge functions de Supabase de forma
 * defensiva.
 *
 * Por qué existe: antes había 6 sitios con el patrón
 *   `${(import.meta.env.VITE_SUPABASE_URL as string).trim()}/functions/v1/...`
 * Ese `.trim()` directo sobre `undefined` crashea al cargar el módulo
 * si las env vars no están configuradas (caso: dev sin `.env.local`,
 * branch preview de Vercel con misconfig, etc). Disparaba `GlobalErrorBoundary`
 * y dejaba toda la app en blanco — no solo la pantalla afectada.
 *
 * Estos helpers retornan strings seguros aunque las env vars falten
 * (devolverán URLs inválidas que fallarán en runtime con mensaje
 * claro, pero NO crashearán el module-load).
 */

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) || '';
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) || '';

/** Detecta si las env vars de Supabase están bien configuradas. Útil
 *  para que componentes que dependen del API muestren un fallback
 *  amistoso en lugar de crashear. */
export function isSupabaseConfigured(): boolean {
  return SUPABASE_URL.trim().length > 0 && SUPABASE_ANON_KEY.trim().length > 0;
}

/** URL completa de una edge function. Devuelve string aunque las env
 *  vars falten — la llamada HTTP fallará en runtime con error claro,
 *  pero no crashea el module-load. */
export function getFunctionUrl(name: string): string {
  const base = SUPABASE_URL.trim();
  if (!base) {
    // Log único para no spamear consola, pero queda evidencia.
    console.warn(`[edgeFunctionUrl] VITE_SUPABASE_URL no configurada (función: ${name})`);
    return `https://placeholder.invalid/functions/v1/${name}`;
  }
  return `${base}/functions/v1/${name}`;
}

/** Variante con apikey en query string. Algunos endpoints (chat-marco)
 *  la usan así para compatibilidad con clientes que no setean header. */
export function getFunctionUrlWithApiKey(name: string): string {
  const url = getFunctionUrl(name);
  const key = SUPABASE_ANON_KEY.trim();
  if (!key) return url;
  return `${url}?apikey=${key}`;
}
